// What the galaxy map puts forward at a glance (issue #37): the player's own network, the capital, the stars a relay
// can reach next, and the camera that frames them on first login. Pure functions over the PlayerView, tested
// without a canvas.
import { hexNeighbors, parseHex, hexKey, type PlayerView } from '@aurane/sim';

export interface NetworkFocus {
  capital: string;
  /** The player's systems: connected ones and those a relay under construction is bringing in. */
  mine: Set<string>;
  /** Unclaimed stars one relay away from the connected network: the next moves. */
  inRange: Set<string>;
  /** Sectors holding one of the player's systems. */
  homeSectors: Set<string>;
  /** The ring of sectors around the home ones (seen or not): the player's neighbourhood (issue #39). */
  nearSectors: Set<string>;
}

/** How much of the map's backdrop a sector gets (issue #39): a lit neighbourhood, a near-invisible grid beyond. */
export type SectorTier = 'home' | 'near' | 'far';
export const sectorTier = (f: Pick<NetworkFocus, 'homeSectors' | 'nearSectors'>, key: string): SectorTier =>
  f.homeSectors.has(key) ? 'home' : f.nearSectors.has(key) ? 'near' : 'far';

export function networkFocus(v: PlayerView): NetworkFocus {
  const mine = new Set<string>();
  const homeSectors = new Set<string>();
  for (const s of v.systems) if (s.owner === v.me.id) { mine.add(s.id); homeSectors.add(s.sector); }
  const inRange = new Set<string>();
  for (const opts of Object.values(v.linkTargets)) for (const o of opts) if (!mine.has(o.to)) inRange.add(o.to);
  const nearSectors = new Set<string>();
  for (const k of homeSectors) for (const n of hexNeighbors(parseHex(k))) { const nk = hexKey(n); if (!homeSectors.has(nk)) nearSectors.add(nk); }
  return { capital: v.me.capital, mine, inRange, homeSectors, nearSectors };
}

export interface Insets { top: number; right: number; bottom: number; left: number }
export interface Frame { scale: number; x: number; y: number }

/**
 * World scale and position that show `points` inside the free part of the screen (minus the HUD and panel
 * `insets`), centred on the first point (the capital) so it never sits at an edge. Zoom is clamped: a lone capital
 * does not fill the screen, a sprawling network does not shrink to dust.
 */
export function frameOn(points: readonly { x: number; y: number }[], screen: { width: number; height: number }, insets: Insets, opts: { min: number; max: number; pad: number }): Frame | null {
  const c = points[0];
  if (!c) return null;
  const freeW = Math.max(120, screen.width - insets.left - insets.right);
  const freeH = Math.max(120, screen.height - insets.top - insets.bottom);
  // Half-extents around the capital, so it stays in the middle of the free area.
  let hx = 0, hy = 0;
  for (const p of points) { hx = Math.max(hx, Math.abs(p.x - c.x)); hy = Math.max(hy, Math.abs(p.y - c.y)); }
  const fit = Math.min((freeW / 2) / (hx + opts.pad), (freeH / 2) / (hy + opts.pad));
  const scale = Math.min(opts.max, Math.max(opts.min, fit));
  const cx = insets.left + freeW / 2, cy = insets.top + freeH / 2;
  return { scale, x: cx - c.x * scale, y: cy - c.y * scale };
}
